import React, { useState } from 'react';
import moment from 'moment-timezone';
import { Button, Modal, message } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { SnoozeOptionList } from './ReminderMenu';
import ReminderDateTimeInput from './ReminderDateTimeInput';
import OWButton from '../ow_button/OWButton';

// A bell with "zz" floating off its top-right: the snooze affordance wherever a due reminder can
// be put off. The z's are positioned outside the layout, so the bell alone is centred in its
// button; they spill into the button's padding. Icon-only: next to a label they'd crowd it.
// The outer span is a flex row like antd's own icon in an icon-only button (antd makes a button's
// child spans inline-block, which would sit the bell on the text baseline, 2px high).
export const SnoozeIcon: React.FC = () => (
	<span className="!flex justify-center">
		<span className="relative inline-flex">
			<BellOutlined className="!flex" />
			<span
				aria-hidden
				className="pointer-events-none absolute -right-[7px] -top-[5px] flex items-start font-bold leading-none"
			>
				<span className="mt-[3px] text-[6px]">z</span>
				<span className="text-[8px]">z</span>
			</span>
		</span>
	</span>
);

interface Options {
	isPersisting: boolean;
	// Saves the new reminder time; resolves true once the backend accepted it.
	snoozeUntil: (until: moment.Moment) => Promise<boolean>;
	// Runs as soon as the user picks an option (e.g. to close the surface the menu came from).
	onPick?: () => void;
}

/**
 * The snooze choices for a due reminder: a dropdown menu of preset delays plus "Custom", which
 * opens a modal with a date/time picker. Returns the menu to hand an antd Dropdown and the
 * modal to render alongside it.
 */
const useReminderSnoozeControls = ({ isPersisting, snoozeUntil, onPick }: Options) => {
	const [customSnoozeOpen, setCustomSnoozeOpen] = useState(false);
	const [customSnoozeAt, setCustomSnoozeAt] = useState<moment.Moment | null>(null);

	const handleSnooze = (minutes: number) => {
		if (onPick) onPick();
		snoozeUntil(moment().add(minutes, 'minutes').second(0).millisecond(0));
	};
	const openCustomSnooze = () => {
		if (onPick) onPick();
		setCustomSnoozeAt(null);
		setCustomSnoozeOpen(true);
	};
	const handleCustomSnooze = () => {
		if (!customSnoozeAt || !customSnoozeAt.isAfter(moment())) {
			message.error('Choose a reminder time in the future.');
			return;
		}
		snoozeUntil(customSnoozeAt).then((ok) => ok && setCustomSnoozeOpen(false));
	};

	const snoozeMenu = (
		<div className="rounded-sm bg-white shadow-notifications-card">
			<SnoozeOptionList onSnooze={handleSnooze} onCustom={openCustomSnooze} />
		</div>
	);

	const customSnoozeModal = (
		<Modal
			visible={customSnoozeOpen}
			title="Snooze reminder until"
			onCancel={() => setCustomSnoozeOpen(false)}
			width={400}
			footer={[
				<Button key="cancel" onClick={() => setCustomSnoozeOpen(false)} disabled={isPersisting}>
					CANCEL
				</Button>,
				<OWButton key="snooze" type="primary" onClick={handleCustomSnooze} loading={isPersisting}>
					SNOOZE
				</OWButton>,
			]}
		>
			<ReminderDateTimeInput value={customSnoozeAt} onChange={setCustomSnoozeAt} defaultOpen />
		</Modal>
	);

	return { snoozeMenu, customSnoozeModal };
};

export default useReminderSnoozeControls;
